// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { cn } from '../lib/utils';

export const ProgressBar = ({ value, className, showLabel = false, size = 'default' }) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedValue(value), 100);
    return () => clearTimeout(timer);
  }, [value]);

  const sizeClasses = {
    small: 'h-1.5',
    default: 'h-2.5',
    large: 'h-3'
  };

  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className="flex justify-between mb-1.5 text-sm">
          <span className="text-[#888780]">Progresso</span>
          <span className="text-[#E8E8E4] font-medium tabular-nums">{Math.round(value)}%</span>
        </div>
      )}
      <div className={cn('w-full sapiens-progress-track', sizeClasses[size])}>
        <div 
          className="h-full sapiens-progress-fill"
          style={{ width: `${animatedValue}%` }}
        />
      </div>
    </div>
  );
};

export const GlobalProgressBar = ({ value }) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedValue(value), 100);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="global-progress-bar">
      <div 
        className="global-progress-bar-fill"
        style={{ width: `${animatedValue}%` }}
      />
    </div>
  );
};

export const LevelBadge = ({ level }) => {
  const levelNames = {
    1: 'Base',
    2: 'Intermedio',
    3: 'Avanzato',
    4: 'Esperto'
  };

  return (
    <span className={`level-badge level-${level}`} data-testid={`level-badge-${level}`}>
      Livello {level} · {levelNames[level]}
    </span>
  );
};

export const StreakCounter = ({ streak }) => {
  const [displayStreak, setDisplayStreak] = useState(0);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      if (current < streak) {
        current++;
        setDisplayStreak(current);
      } else {
        clearInterval(interval);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [streak]);

  return (
    <div 
      className="flex items-center gap-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 px-4 py-2 rounded-lg" 
      data-testid="streak-counter"
    >
      <span className="text-xl">🔥</span>
      <span className="text-[#C9A84C] font-medium tabular-nums text-lg">{displayStreak}</span>
      <span className="text-[#888780] text-sm">giorni</span>
    </div>
  );
};

export const BadgeDisplay = ({ badge, earned = false }) => {
  const badgeInfo = {
    primo_passo: { icon: '🎯', name: 'Primo passo', desc: 'Prima lezione completata' },
    studente_base: { icon: '📚', name: 'Studente base', desc: 'Livello 1 completato' },
    investitore_junior: { icon: '📈', name: 'Investitore junior', desc: 'Livello 2 completato' },
    esperto_portafoglio: { icon: '💼', name: 'Esperto portafoglio', desc: 'Livello 3 completato' },
    maestro_finanza: { icon: '🏆', name: 'Maestro della finanza', desc: 'Tutti i livelli completati' },
    quiz_master: { icon: '🧠', name: 'Quiz master', desc: '5 quiz con 80%+' },
    perfezionista: { icon: '💯', name: 'Perfezionista', desc: 'Quiz perfetto' },
    costante: { icon: '📅', name: 'Costante', desc: '7 giorni consecutivi' },
    maratoneta: { icon: '🏃', name: 'Maratoneta', desc: '30 giorni consecutivi' },
  };

  const info = badgeInfo[badge] || { icon: '🏅', name: badge, desc: '' };

  return (
    <div 
      className={cn(
        'flex flex-col items-center gap-2 p-4 rounded-xl border transition-all',
        earned 
          ? 'bg-[#C9A84C]/10 border-[#C9A84C]/30' 
          : 'bg-[#252523] border-white/[0.08] opacity-40 grayscale'
      )}
      data-testid={`badge-${badge}`}
    >
      <span className="text-3xl">{info.icon}</span>
      <span className="text-[#E8E8E4] font-medium text-xs text-center">{info.name}</span>
      <span className="text-[10px] text-[#888780] text-center leading-tight">{info.desc}</span>
    </div>
  );
};

export const PremiumBanner = ({ onUpgrade }) => {
  return (
    <div className="sapiens-card p-6 border-[#C9A84C]/30" data-testid="premium-banner">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="text-3xl">👑</span>
          <div>
            <h3 className="text-[#E8E8E4] font-medium text-lg">Passa a SAPIENS Premium</h3>
            <p className="text-sm text-[#888780]">
              €4,99/mese · Prezzo Fondatori fino al 31 agosto 2026
            </p>
          </div>
        </div>
        <button 
          onClick={onUpgrade}
          className="sapiens-btn-primary px-6 py-2 font-medium"
          style={{ background: '#C9A84C', color: '#1C1C1A', borderRadius: 8, fontWeight: 600 }}
          data-testid="premium-banner-cta"
        >
          Scopri SAPIENS Premium
        </button>
      </div>
    </div>
  );
};

export const StatCard = ({ icon, value, label, variant = 'neutral' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const numericValue = typeof value === 'number' ? value : parseInt(value) || 0;

  useEffect(() => {
    let current = 0;
    const increment = Math.ceil(numericValue / 30);
    const interval = setInterval(() => {
      if (current < numericValue) {
        current = Math.min(current + increment, numericValue);
        setDisplayValue(current);
      } else {
        clearInterval(interval);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [numericValue]);

  const valueColors = {
    neutral: 'text-[#E8E8E4]',
    positive: 'text-[#3D7A52]',
    gold: 'text-[#C9A84C]'
  };

  return (
    <div className="text-center animate-count">
      <div className="text-2xl mb-2">{icon}</div>
      <div className={`text-3xl font-medium tabular-nums ${valueColors[variant]}`}>
        {typeof value === 'number' ? displayValue.toLocaleString('it-IT') : value}
      </div>
      <div className="text-sm text-[#888780]">{label}</div>
    </div>
  );
};
